import {
    Divider,
    Flex,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useTheme,
} from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { updateAdditionalInfo } from '../../canopy-ui-components/api/transactions';
import CanopyButton from '../../canopy-ui-components/components/CanopyButton/CanopyButton';
import ErrorBox from '../../canopy-ui-components/components/ErrorBox/ErrorBox';
import CanopyIconButton from '../../canopy-ui-components/components/IconButton/IconButton';
import * as ga from '../../canopy-ui-components/google-analytics';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import EditInfo from './EditInfo';

const EditInfoModal = ({
    data,
    showModal,
    setShowModal,
    globalProps,
    refreshData,
}: {
    data: any;
    showModal: boolean;
    setShowModal: (flag: boolean) => void;
    globalProps: GlobalProps;
    refreshData: () => void;
}) => {
    const currentColors = useCurrentColors();
    const { t } = globalProps;
    const [isLoading, setIsLoading] = useState(false);
    const [inputInfo, setInputInfo] = useState<any>([]);
    const [error, setError] = useState<string | boolean>();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const pastInfo = useMemo(() => {
        if (data?.additional_info) {
            return Object.entries(data?.additional_info).map(
                ([key, value]: any) => ({
                    key,
                    value,
                })
            );
        } else {
            return [];
        }
    }, [data.additional_info]);
    const theme = useTheme();

    const handleOnClose = () => {
        ga.event({
            action: `${window.location.pathname.replace(
                '//g/',
                ''
            )} > Close Edit Additional Info`,
        });
        reAssignAdditionalInfo(data);
        setShowModal(false);
    };

    useEffect(() => {
        showModal ? onOpen() : onClose();
    }, [showModal]);

    const handleInfoUpdate = async () => {
        ga.event({
            action: `${window.location.pathname.replace(
                '//g/',
                ''
            )} > Edit Additional Info`,
        });
        setIsLoading(true);
        const objValues: any = {};
        inputInfo.additionalInfo.forEach((item: any) => {
            objValues[item.key] = item.value;
        });
        const params = {
            blotter_id: inputInfo.blotter_id,
            additional_info: objValues,
        };
        try {
            let response: any = await updateAdditionalInfo(params);
            if (response.error) {
                setError(response.error);
            } else if (response.success == true) {
                setError(false);
                setTimeout(() => {
                    refreshData();
                }, 2000);
            }
        } catch (err: any) {
            setError(
                t(err?.response?.data?.error) || err?.response?.data?.message
            );
        } finally {
            setIsLoading(false);
        }
    };

    const reAssignAdditionalInfo = (data: any) => {
        let additionalInfo: any = [];
        if (data?.additional_info) {
            additionalInfo = Object.entries(data?.additional_info).map(
                ([key, value]: any) => ({
                    key,
                    value,
                })
            );
        }
        setInputInfo({ ...data, additionalInfo });
    };

    const isUpdateDisabled = () =>
        isEqual(inputInfo?.additionalInfo, pastInfo) ||
        (!isEqual(inputInfo?.additionalInfo, pastInfo) &&
            inputInfo?.additionalInfo?.some(
                (obj: any) => !obj.key || !obj.value
            ));

    useEffect(() => {
        reAssignAdditionalInfo(data);
    }, [data]);
    return (
        <Modal
            isCentered
            closeOnOverlayClick={false}
            onEsc={() => {
                setShowModal(false);
                reAssignAdditionalInfo(data); //onEsc we're re-assigning the previous data of the modal
            }}
            isOpen={isOpen}
            size="6xl"
            onClose={onClose}>
            <ModalOverlay
                style={{
                    backdropFilter: 'blur(5px)',
                }}
            />
            <ModalContent>
                <ModalHeader
                    color={currentColors.brand['10']}
                    fontSize="20px"
                    fontWeight="bold"
                    padding="15px">
                    <Flex alignItems="center">
                        <Image
                            src={theme?.images?.headerLogo}
                            maxWidth="100%"
                            width="auto"
                            height={'35px'}
                            mr="17.5px"
                        />
                        <Text width="100%">
                            {t('ui.mm.tooltip.edit-additional-info', {
                                count: inputInfo?.length,
                            })}
                        </Text>
                    </Flex>
                    <Divider
                        borderColor={currentColors.brand['700']}
                        width="100%"
                        mb="0px"
                    />
                </ModalHeader>
                {
                    <ModalCloseButton
                        as={() => (
                            <CanopyIconButton
                                background="transparent"
                                cursor="pointer"
                                border="none"
                                display="inline-flex"
                                onClick={handleOnClose}
                                color={currentColors.brand['500']}
                                width="24px"
                                height="24px"
                                aria-label="remove"
                                icon="ActClose"
                                variant="ghost"
                                position="absolute"
                                top="15px"
                                right="15px"
                                isDisabled={isLoading}
                            />
                        )}
                    />
                }
                <ModalBody
                    padding="0 15px"
                    maxHeight="60vh"
                    overflow="auto">
                    <EditInfo
                        inputInfo={inputInfo}
                        globalProps={globalProps}
                        handleEditInfo={(
                            field: string,
                            value: string,
                            index: number
                        ) => {
                            const additionalInfo: any =
                                inputInfo.additionalInfo || {};
                            additionalInfo[index][field] = value;
                            setInputInfo({ ...inputInfo, additionalInfo });
                        }}
                        addNewInfo={() => {
                            inputInfo.additionalInfo
                                ? setInputInfo({
                                      ...inputInfo,
                                      additionalInfo: [
                                          ...(inputInfo.additionalInfo || {}),
                                          { key: null, value: null },
                                      ],
                                  })
                                : setInputInfo({
                                      ...inputInfo,
                                      additionalInfo: [
                                          { key: null, value: null },
                                      ],
                                  });
                        }}
                        deleteInfo={(index: number) => {
                            setInputInfo({
                                ...inputInfo,
                                additionalInfo: inputInfo.additionalInfo.filter(
                                    (item: any, i: number) => index !== i
                                ),
                            });
                        }}></EditInfo>
                </ModalBody>

                <ModalFooter
                    flexDirection="column"
                    p="15px">
                    <Divider
                        mt="0"
                        mb="15px"
                    />
                    <HStack
                        justify="flex-end"
                        width="100%"
                        spacing="15px">
                        {error === false ? (
                            <ErrorBox
                                p="0"
                                m="0"
                                color={currentColors.contentText}
                                backgroundColor="inherit"
                                fontSize="14px"
                                fontWeight="700"
                                position="relative"
                                mb="20px"
                                textAlign="center"
                                error={t(
                                    'ui.additional-info-update-success'
                                )}></ErrorBox>
                        ) : typeof error === 'string' ? (
                            <ErrorBox
                                p="0"
                                m="0"
                                backgroundColor="inherit"
                                color={currentColors.errorText}
                                fontSize="14px"
                                fontWeight="700"
                                position="relative"
                                mb="20px"
                                textAlign="center"
                                error={error}></ErrorBox>
                        ) : null}
                        <CanopyButton
                            onClick={handleInfoUpdate}
                            data-test="uploadButton"
                            width="120px"
                            height="30px"
                            isLoading={isLoading}
                            isDisabled={isUpdateDisabled()}
                            fontSize="sm">
                            {t('ui.mm.update')}
                        </CanopyButton>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditInfoModal;
